import { HorizontalStack, TooltipPopOver } from '@ztechapps/webui.util'
import { BusyButton } from '@ztechapps/webui.button'
import { getWindow } from '@ztechapps/common.utils'

type DownloadBlockProps = {
  children: string
}

/**
 * DownloadBlock component renders a download card for file data embedded in markdown.
 * Parses the special download code block format and provides a download button.
 *
 * Expected format:
 * ```download
 * filename: report.csv
 * mimetype: text/csv
 * data:
 * <actual file content here>
 * ```
 */
export const DownloadBlock = ({ children }: DownloadBlockProps) => {
  const lines = children.trim().split('\n')
  let filename = 'download.txt'
  let mimetype = 'text/plain'
  let dataStartIndex = 0

  // Parse metadata lines
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    if (line.startsWith('filename:')) {
      filename = line.substring('filename:'.length).trim()
    } else if (line.startsWith('mimetype:')) {
      mimetype = line.substring('mimetype:'.length).trim()
    } else if (line.startsWith('data:')) {
      dataStartIndex = i + 1
      break
    }
  }

  // Extract data content (everything after "data:" line)
  const data = lines.slice(dataStartIndex).join('\n')

  const handleDownload = () => {
    const window = getWindow()
    const blob = new Blob([data], { type: mimetype })
    const url = URL.createObjectURL(blob)
    const a = window.document.createElement('a')
    a.href = url
    a.download = filename
    window.document.body.appendChild(a)
    a.click()
    window.document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // Get file extension for display
  const extension = filename.split('.').pop()?.toUpperCase() ?? 'FILE'

  return (
    <HorizontalStack gap={2} className="align-items-center p-3 border rounded-3 bg-body m-0">
      {/* File info */}
      <div className="flex-grow-1 overflow-hidden">
        <TooltipPopOver tooltipBody={filename}>
          <div className="fw-semibold text-truncate">{filename}</div>
        </TooltipPopOver>
        <div className="small text-muted">
          {extension} • {mimetype}
        </div>
      </div>

      {/* Download button */}
      <BusyButton onClick={handleDownload} icon="download" text="Download" />
    </HorizontalStack>
  )
}
